// Paying for a booking, the same for a card and a wallet (Apple Pay, Google
// Pay): the server books and charges, the bank may ask to verify the payment
// first, and every failure ends as a message the guest can read.

// Repeats a request that failed for a moment, e.g. while the server is
// being deployed. A refused request (voucher, validation) stays refused.
export const withRetries = async (
  request,
  delays = [1000, 3000],
  wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
) => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await request();
    } catch (error) {
      const status = error?.response?.status;
      const refused = status >= 400 && status < 500 && status !== 429;
      if (refused || attempt >= delays.length) throw error;
      await wait(delays[attempt]);
    }
  }
};

// Field errors of the contact details and the terms, before any payment.
export const contactErrors = (userDetails, termsAccepted, t) => {
  const required = [t("field_required", "This field is required")];
  const errors = {};
  if (!userDetails.name.trim()) errors["userDetails.name"] = required;
  if (!userDetails.lastName.trim()) errors["userDetails.lastName"] = required;
  if (!userDetails.email.trim()) {
    errors["userDetails.email"] = required;
  } else if (!/\S+@\S+\.\S+/.test(userDetails.email)) {
    errors["userDetails.email"] = [t("invalid_email", "Please enter a valid email address")];
  }
  if (!userDetails.phone.trim()) errors["userDetails.phone"] = required;
  if (!termsAccepted) {
    errors.terms = [t("accept", "Please accept the terms and conditions")];
  }

  return errors;
};

// Field errors of the card form.
export const cardErrors = (cardholderName, cardFieldStatus, t) => {
  const required = [t("field_required", "This field is required")];
  const errors = {};
  if (!String(cardholderName || "").trim()) errors["card.name"] = required;
  if (!cardFieldStatus.cardNumber.complete) errors["card.number"] = required;
  if (!cardFieldStatus.cardExpiry.complete) errors["card.expiry"] = required;
  if (!cardFieldStatus.cardCvc.complete) errors["card.cvc"] = required;

  return errors;
};

// Whether the card form is filled in completely, so paying by card can start.
export const isCardComplete = (cardholderName, fields) =>
  Boolean(String(cardholderName || "").trim()) &&
  Boolean(fields?.cardNumber && fields?.cardExpiry && fields?.cardCvc);

// The message of a failed request: the server's `errors.msg`, its `message`
// text, or the fallback.
export const responseMessage = (data, fallback) => {
  const message = data?.errors?.msg;
  if (Array.isArray(message) && message[0]) return String(message[0]);
  if (typeof message === "string" && message) return message;
  if (typeof data?.message === "string" && data.message) return data.message;

  return fallback;
};

// Field errors the server returned, without the general message.
export const responseFieldErrors = (data) => {
  const { msg, ...fields } = data?.errors || {};

  return fields;
};

// Books and pays with a Stripe payment method. Returns
// { status: "success" }, { status: "price_changed", price, message } or
// { status: "error", message, fieldErrors }.
export const payReservation = async ({
  submit,
  complete,
  stripe,
  paymentMethodId,
  expectedAmount,
  messages,
}) => {
  let response;
  try {
    response = await submit(paymentMethodId, expectedAmount);
  } catch (error) {
    const data = error?.response?.data;
    if (error?.response?.status === 409) {
      return {
        status: "price_changed",
        price: data?.price,
        message: messages.priceChanged,
      };
    }

    return {
      status: "error",
      message: responseMessage(data, messages.failed),
      fieldErrors: responseFieldErrors(data),
    };
  }

  if (response?.status !== "requires_action") {
    return { status: "success" };
  }

  // The bank verifies the payment (3D Secure), then the server books the stay.
  const verify = stripe.handleNextAction
    ? stripe.handleNextAction({ clientSecret: response.client_secret })
    : stripe.handleCardAction(response.client_secret);
  const { error } = await verify;
  if (error) {
    return { status: "error", message: error.message || messages.authenticationFailed };
  }

  try {
    await complete(response.payment_intent_id);
  } catch (completeError) {
    return {
      status: "error",
      message: responseMessage(completeError?.response?.data, messages.authenticationFailed),
    };
  }

  return { status: "success" };
};

// Stripe Elements locale of an app language; "auto" lets Stripe decide.
const STRIPE_LOCALES = {
  bg: "bg",
  cz: "cs",
  de: "de",
  en: "en",
  hr: "hr",
  hu: "hu",
  it: "it",
  pl: "pl",
  ro: "ro",
  si: "sl",
  sk: "sk",
};

export const stripeLocale = (language) =>
  STRIPE_LOCALES[String(language || "").slice(0, 2).toLowerCase()] || "auto";
